/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  FlatList,
  Alert,
  StatusBar,
  Dimensions,
  Modal,
  Animated,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';

const { width } = Dimensions.get('window');

const VirtualInternshipScreen = () => {
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [menuVisible, setMenuVisible] = useState(false);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));

  // Enhanced gradient color schemes
  const gradients = {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
  };

  useEffect(() => {
    startAnimations();
  }, []);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const categories = ['All', 'Engineering', 'Technology', 'Finance', 'Marketing', 'Design'];
  const difficulties = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const menuItems = [
    { id: 1, title: 'Learning Dashboard', icon: 'view-dashboard', screen: 'LearningDashboard' },
    { id: 2, title: 'My Courses', icon: 'book-open-variant', screen: 'LearningCoursesScreen' },
    { id: 3, title: 'Engineering Books', icon: 'book', screen: 'EngineeringBooksScreen' },
    { id: 4, title: 'Virtual Internships', icon: 'briefcase', screen: 'VirtualInternshipScreen', active: true },
    { id: 5, title: 'Learning History', icon: 'history', screen: 'MyLearningHistoryScreen' },
    { id: 6, title: 'Profile', icon: 'account', screen: 'LearningProfileScreen' },
    { id: 7, title: 'Search', icon: 'magnify', screen: 'SearchScreen' },
    { id: 8, title: 'More', icon: 'dots-horizontal', screen: 'MoreScreen' },
  ];

  const internships = [
    {
      id: '1',
      title: 'Civil Engineering Virtual Experience',
      company: 'Arup Group',
      duration: '12-16 hours',
      difficulty: 'Intermediate',
      category: 'Engineering',
      description: 'Design and analyze structural systems for real-world infrastructure projects.',
      modules: [
        { id: '1a', title: 'Structural Analysis Fundamentals', status: 'completed' },
        { id: '1b', title: 'Bridge Design Project', status: 'in-progress' },
        { id: '1c', title: 'Building Information Modeling', status: 'not-started' },
        { id: '1d', title: 'Sustainability Assessment', status: 'not-started' },
      ],
      skills: ['AutoCAD', 'Structural Analysis', 'BIM', 'Sustainability'],
      benefits: ['Certificate', 'Portfolio Project', 'Industry Mentorship'],
      rating: 4.8,
      participants: 2340,
      gradient: gradients.orange,
      icon: 'bridge',
    },
    {
      id: '2',
      title: 'Software Engineering Virtual Experience',
      company: 'J.P. Morgan Chase & Co.',
      duration: '10-15 hours',
      difficulty: 'Beginner',
      category: 'Technology',
      description: 'Build financial technology solutions and learn about fintech industry.',
      modules: [
        { id: '2a', title: 'Interface with JPMC Environment', status: 'completed' },
        { id: '2b', title: 'Financial Services Overview', status: 'completed' },
        { id: '2c', title: 'Stock Price Data Feed', status: 'in-progress' },
        { id: '2d', title: 'Software Engineering Task', status: 'not-started' },
      ],
      skills: ['Python', 'React', 'Financial Analysis', 'Git'],
      benefits: ['Certificate', 'Resume Boost', 'Networking'],
      rating: 4.7,
      participants: 5670,
      gradient: gradients.blue,
      icon: 'bank',
    },
    {
      id: '3',
      title: 'Mechanical Engineering Virtual Experience',
      company: 'Rolls-Royce',
      duration: '14-18 hours',
      difficulty: 'Advanced',
      category: 'Engineering',
      description: 'Design and optimize aerospace engine components using advanced engineering principles.',
      modules: [
        { id: '3a', title: 'Thermodynamics in Aerospace', status: 'completed' },
        { id: '3b', title: 'Engine Component Design', status: 'in-progress' },
        { id: '3c', title: 'Materials Selection', status: 'not-started' },
        { id: '3d', title: 'Performance Optimization', status: 'not-started' },
      ],
      skills: ['SolidWorks', 'ANSYS', 'Thermodynamics', 'Materials Science'],
      benefits: ['Certificate', 'Industry Project', 'Expert Feedback'],
      rating: 4.9,
      participants: 1890,
      gradient: gradients.green,
      icon: 'airplane',
    },
    {
      id: '4',
      title: 'Data Science Virtual Experience',
      company: 'Accenture',
      duration: '8-12 hours',
      difficulty: 'Intermediate',
      category: 'Technology',
      description: 'Analyze real-world datasets and build predictive models for business insights.',
      modules: [
        { id: '4a', title: 'Data Cleaning & Preparation', status: 'completed' },
        { id: '4b', title: 'Exploratory Data Analysis', status: 'in-progress' },
        { id: '4c', title: 'Machine Learning Models', status: 'not-started' },
      ],
      skills: ['Python', 'R', 'Tableau', 'Machine Learning'],
      benefits: ['Certificate', 'Industry Insight', 'Portfolio Project'],
      rating: 4.6,
      participants: 3450,
      gradient: gradients.purple,
      icon: 'chart-line',
    },
    {
      id: '5',
      title: 'Electrical Engineering Virtual Experience',
      company: 'Siemens',
      duration: '10-14 hours',
      difficulty: 'Intermediate',
      category: 'Engineering',
      description: 'Design electrical systems and work on renewable energy projects.',
      modules: [
        { id: '5a', title: 'Circuit Design Fundamentals', status: 'completed' },
        { id: '5b', title: 'Power Systems Analysis', status: 'in-progress' },
        { id: '5c', title: 'Renewable Energy Systems', status: 'not-started' },
      ],
      skills: ['MATLAB', 'Circuit Design', 'Power Systems', 'Renewable Energy'],
      benefits: ['Certificate', 'Technical Skills', 'Industry Exposure'],
      rating: 4.7,
      participants: 2100,
      gradient: gradients.info,
      icon: 'lightning-bolt',
    },
    {
      id: '6',
      title: 'Digital Marketing Virtual Experience',
      company: 'Google',
      duration: '15-20 hours',
      difficulty: 'Beginner',
      category: 'Marketing',
      description: 'Create comprehensive digital marketing campaigns and analyze their performance.',
      modules: [
        { id: '6a', title: 'SEO & Content Strategy', status: 'completed' },
        { id: '6b', title: 'Social Media Marketing', status: 'completed' },
        { id: '6c', title: 'Email Marketing Campaigns', status: 'in-progress' },
        { id: '6d', title: 'Analytics & Optimization', status: 'not-started' },
      ],
      skills: ['Google Analytics', 'SEO', 'Social Media', 'Content Marketing'],
      benefits: ['Google Certificate', 'Campaign Portfolio', 'Industry Recognition'],
      rating: 4.5,
      participants: 4200,
      gradient: gradients.danger,
      icon: 'bullhorn',
    },
    {
      id: '7',
      title: 'UX/UI Design Virtual Experience',
      company: 'Adobe',
      duration: '12-16 hours',
      difficulty: 'Beginner',
      category: 'Design',
      description: 'Learn user experience design principles and create compelling digital interfaces.',
      modules: [
        { id: '7a', title: 'Design Thinking Process', status: 'completed' },
        { id: '7b', title: 'User Research Methods', status: 'in-progress' },
        { id: '7c', title: 'Prototyping & Testing', status: 'not-started' },
      ],
      skills: ['Adobe XD', 'Figma', 'User Research', 'Prototyping'],
      benefits: ['Adobe Certificate', 'Design Portfolio', 'Creative Skills'],
      rating: 4.6,
      participants: 2800,
      gradient: gradients.pink,
      icon: 'palette',
    },
    {
      id: '8',
      title: 'Financial Analysis Virtual Experience',
      company: 'Goldman Sachs',
      duration: '8-12 hours',
      difficulty: 'Advanced',
      category: 'Finance',
      description: 'Analyze financial markets and develop investment strategies using real market data.',
      modules: [
        { id: '8a', title: 'Financial Modeling', status: 'completed' },
        { id: '8b', title: 'Risk Assessment', status: 'in-progress' },
        { id: '8c', title: 'Investment Strategy', status: 'not-started' },
      ],
      skills: ['Excel', 'Financial Modeling', 'Risk Analysis', 'Bloomberg Terminal'],
      benefits: ['Certificate', 'Finance Skills', 'Industry Network'],
      rating: 4.8,
      participants: 1650,
      gradient: gradients.teal,
      icon: 'chart-pie',
    },
  ];

  const filteredInternships = internships.filter(internship => {
    const matchesSearch = String(internship.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(internship.company ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         internship.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || internship.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'All' || internship.difficulty === selectedDifficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const handleMenuPress = () => {
    setMenuVisible(true);
  };

  const handleMenuItemPress = (item) => {
    setMenuVisible(false);
    if (item.screen && item.screen !== 'VirtualInternshipScreen') {
      navigation.navigate(item.screen);
    }
  };

  const handleApplyInternship = (internship) => {
    Alert.alert(
      '🎯 Apply for Internship',
      `Would you like to apply for "${internship.title}" at ${internship.company}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: '✅ Apply',
          onPress: () => {
            Alert.alert('🎉 Success', 'Your application has been submitted! You will receive further instructions via email.');
          },
        },
      ]
    );
  };

  const handleViewDetails = (internship) => {
    navigation.navigate('InternshipDetails', { internship });
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesome5 key={i} name="star" size={14} color="#fbbf24" solid />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesome5 key="half" name="star-half-alt" size={14} color="#fbbf24" solid />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <FontAwesome5 key={`empty-${i}`} name="star" size={14} color="#e5e7eb" />
      );
    }

    return stars;
  };

  const getModuleProgress = (modules) => {
    const completed = modules.filter(module => module.status === 'completed').length;
    return Math.round((completed / modules.length) * 100);
  };

  const renderInternshipCard = ({ item }) => (
    <Animated.View
      style={[
        styles.internshipCard,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <View style={styles.cardHeader}>
        <LinearGradient
          colors={item.gradient}
          style={styles.companyIconGradient}
        >
          <MaterialCommunityIcons name={item.icon} size={36} color="#ffffff" />
        </LinearGradient>
        <View style={styles.headerInfo}>
          <Text style={styles.internshipTitle} numberOfLines={2}>
            {item.title}
          </Text>
          <Text style={styles.companyName}>🏢 {item.company}</Text>
          <View style={styles.ratingContainer}>
            <View style={styles.starsContainer}>
              {renderStars(item.rating)}
            </View>
            <Text style={styles.ratingText}>
              {item.rating} (👥 {item.participants.toLocaleString()})
            </Text>
          </View>
        </View>
      </View>

      <Text style={styles.description} numberOfLines={3}>
        {item.description}
      </Text>

      <View style={styles.metaInfo}>
        <View style={styles.metaItem}>
          <LinearGradient
            colors={item.gradient}
            style={styles.metaIconGradient}
          >
            <MaterialCommunityIcons name="clock" size={14} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.metaText}>⏱️ {item.duration}</Text>
        </View>
        <View style={styles.metaItem}>
          <LinearGradient
            colors={item.gradient}
            style={styles.metaIconGradient}
          >
            <MaterialCommunityIcons name="signal" size={14} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.metaText}>📊 {item.difficulty}</Text>
        </View>
        <View style={styles.metaItem}>
          <LinearGradient
            colors={item.gradient}
            style={styles.metaIconGradient}
          >
            <MaterialCommunityIcons name="account-group" size={14} color="#ffffff" />
          </LinearGradient>
          <Text style={styles.metaText}>👥 {item.participants.toLocaleString()}</Text>
        </View>
      </View>

      <View style={styles.progressSection}>
        <Text style={styles.progressTitle}>
          🎯 Progress: {getModuleProgress(item.modules)}%
        </Text>
        <View style={styles.progressBar}>
          <LinearGradient
            colors={item.gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[styles.progressFill, { width: `${getModuleProgress(item.modules)}%` }]}
          />
        </View>
        <Text style={styles.moduleCount}>
          ✅ {item.modules.filter(m => m.status === 'completed').length} of {item.modules.length} modules completed
        </Text>
      </View>

      <View style={styles.skillsContainer}>
        <Text style={styles.skillsTitle}>🔧 Skills you'll learn:</Text>
        <View style={styles.skillsGrid}>
          {item.skills.slice(0, 4).map((skill, index) => (
            <View key={index} style={styles.skillTag}>
              <LinearGradient
                colors={item.gradient}
                style={styles.skillTagGradient}
              >
                <Text style={styles.skillText}>{skill}</Text>
              </LinearGradient>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.benefitsContainer}>
        <Text style={styles.benefitsTitle}>🎁 What you'll get:</Text>
        <View style={styles.benefitsList}>
          {item.benefits.map((benefit, index) => (
            <View key={index} style={styles.benefitItem}>
              <LinearGradient
                colors={item.gradient}
                style={styles.benefitIconGradient}
              >
                <MaterialCommunityIcons name="check" size={12} color="#ffffff" />
              </LinearGradient>
              <Text style={styles.benefitText}>{benefit}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.cardActions}>
        <TouchableOpacity
          style={styles.detailsButton}
          onPress={() => handleViewDetails(item)}
        >
          <LinearGradient
            colors={item.gradient}
            style={styles.detailsButtonGradient}
          >
            <MaterialCommunityIcons name="information" size={16} color="#ffffff" />
            <Text style={styles.detailsButtonText}>📋 Details</Text>
          </LinearGradient>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={styles.applyButton}
          onPress={() => handleApplyInternship(item)}
        >
          <LinearGradient
            colors={item.gradient}
            style={styles.applyButtonGradient}
          >
            <MaterialCommunityIcons name="send" size={16} color="#ffffff" />
            <Text style={styles.applyButtonText}>🚀 Apply Now</Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  const renderHeader = () => (
    <Animated.View
      style={{
        opacity: fadeAnim,
        transform: [{ translateY: slideAnim }],
      }}
    >
      {/* Enhanced Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchBar}>
          <LinearGradient
            colors={gradients.primary}
            style={styles.searchIconGradient}
          >
            <MaterialCommunityIcons name="magnify" size={20} color="#ffffff" />
          </LinearGradient>
          <TextInput
            style={styles.searchInput}
            placeholder="🔍 Search internships, companies, or skills..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#6b7280"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <LinearGradient
                colors={gradients.danger}
                style={styles.clearIconGradient}
              >
                <MaterialCommunityIcons name="close" size={16} color="#ffffff" />
              </LinearGradient>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Enhanced Category Tabs */}
      <View style={styles.categoryContainer}>
        <FlatList
          data={categories}
          horizontal
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item) => item}
          contentContainerStyle={styles.categoryContent}
          renderItem={({ item: category }) => (
            <TouchableOpacity
              style={styles.categoryTab}
              onPress={() => setSelectedCategory(category)}
            >
              {selectedCategory === category ? (
                <LinearGradient
                  colors={gradients.primary}
                  style={styles.categoryTabGradient}
                >
                  <Text style={styles.activeCategoryTabText}>{category}</Text>
                </LinearGradient>
              ) : (
                <Text style={styles.categoryTabText}>{category}</Text>
              )}
            </TouchableOpacity>
          )}
        />
      </View>

      {/* Enhanced Difficulty Filter */}
      <View style={styles.difficultyContainer}>
        <Text style={styles.filterLabel}>📊 Difficulty Level:</Text>
        <FlatList
          data={difficulties}
          horizontal
          showsHorizontalScrollIndicator={false}
          keyExtractor={(item) => item}
          contentContainerStyle={styles.difficultyContent}
          renderItem={({ item: difficulty }) => (
            <TouchableOpacity
              style={styles.difficultyTab}
              onPress={() => setSelectedDifficulty(difficulty)}
            >
              {selectedDifficulty === difficulty ? (
                <LinearGradient
                  colors={gradients.warning}
                  style={styles.difficultyTabGradient}
                >
                  <Text style={styles.activeDifficultyTabText}>{difficulty}</Text>
                </LinearGradient>
              ) : (
                <Text style={styles.difficultyTabText}>{difficulty}</Text>
              )}
            </TouchableOpacity>
          )}
        />
      </View>

      {/* Enhanced Results Summary */}
      <View style={styles.resultsContainer}>
        <LinearGradient
          colors={gradients.info}
          style={styles.resultsGradient}
        >
          <MaterialCommunityIcons name="briefcase-search" size={20} color="#ffffff" />
          <Text style={styles.resultsText}>
            🎯 {filteredInternships.length} virtual internship{filteredInternships.length !== 1 ? 's' : ''} found
          </Text>
        </LinearGradient>
      </View>
    </Animated.View>
  );

  const renderMenuModal = () => (
    <Modal
      visible={menuVisible}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setMenuVisible(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setMenuVisible(false)}
      >
        <LinearGradient
          colors={gradients.primary}
          style={styles.menuContainer}
        >
          <View style={styles.menuHeader}>
            <Text style={styles.menuTitle}>🧭 Navigation Menu</Text>
            <TouchableOpacity 
              onPress={() => setMenuVisible(false)}
              style={styles.closeButton}
            >
              <LinearGradient
                colors={gradients.danger}
                style={styles.closeButtonGradient}
              >
                <MaterialCommunityIcons name="close" size={20} color="#ffffff" />
              </LinearGradient>
            </TouchableOpacity>
          </View>
          
          <FlatList
            data={menuItems}
            keyExtractor={(item) => item.id.toString()}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[
                  styles.menuItem,
                  item.active && styles.activeMenuItem
                ]}
                onPress={() => handleMenuItemPress(item)}
              >
                <LinearGradient
                  colors={item.active ? gradients.orange : gradients.info}
                  style={styles.menuItemIconGradient}
                >
                  <MaterialCommunityIcons 
                    name={item.icon} 
                    size={20} 
                    color="#ffffff"
                  />
                </LinearGradient>
                <Text style={[
                  styles.menuItemText,
                  item.active && styles.activeMenuItemText
                ]}>
                  {item.title}
                </Text>
                {!item.active && (
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#ffffff" />
                )}
              </TouchableOpacity>
            )}
          />
        </LinearGradient>
      </TouchableOpacity>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {/* Enhanced Header with Back Button */}
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={styles.header}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={28} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>💼 Virtual Internships</Text>
        <TouchableOpacity
          onPress={handleMenuPress}
          style={styles.menuButton}
        >
          <View style={styles.hamburgerContainer}>
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
          </View>
        </TouchableOpacity>
      </LinearGradient>

      {/* Main Content - Using FlatList instead of ScrollView to avoid nesting */}
      <FlatList
        data={filteredInternships}
        renderItem={renderInternshipCard}
        keyExtractor={item => item.id}
        ListHeaderComponent={renderHeader}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.container}
        ListEmptyComponent={() => (
          <Animated.View 
            style={[
              styles.emptyContainer,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            <LinearGradient
              colors={gradients.info}
              style={styles.emptyIconGradient}
            >
              <MaterialCommunityIcons name="briefcase-search" size={64} color="#ffffff" />
            </LinearGradient>
            <Text style={styles.emptyTitle}>🔍 No internships found</Text>
            <Text style={styles.emptyText}>
              Try adjusting your search criteria or filters
            </Text>
          </Animated.View>
        )}
      />

      {/* Enhanced Menu Modal */}
      {renderMenuModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  menuButton: {
    padding: 8,
  },
  hamburgerContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  hamburgerLine: {
    width: 20,
    height: 2,
    backgroundColor: '#ffffff',
    marginVertical: 2,
    borderRadius: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  menuContainer: {
    width: width * 0.85,
    height: '100%',
    paddingTop: 50,
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  menuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
  },
  menuTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  closeButton: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  closeButtonGradient: {
    padding: 8,
    borderRadius: 16,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  activeMenuItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  menuItemIconGradient: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  menuItemText: {
    flex: 1,
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '500',
  },
  activeMenuItemText: {
    fontWeight: '700',
  },
  container: {
    padding: 20,
  },
  searchContainer: {
    marginBottom: 20,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  searchIconGradient: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#374151',
  },
  clearIconGradient: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  categoryContainer: {
    marginBottom: 20,
  },
  categoryContent: {
    paddingHorizontal: 5,
  },
  categoryTab: {
    marginHorizontal: 6,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  categoryTabGradient: {
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  categoryTabText: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '600',
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#ffffff',
  },
  activeCategoryTabText: {
    color: '#ffffff',
    fontWeight: '700',
  },
  difficultyContainer: {
    marginBottom: 20,
  },
  filterLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 12,
  },
  difficultyContent: {
    paddingHorizontal: 5,
  },
  difficultyTab: {
    marginHorizontal: 4,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  difficultyTabGradient: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  difficultyTabText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '600',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#ffffff',
  },
  activeDifficultyTabText: {
    color: '#ffffff',
    fontWeight: '700',
  },
  resultsContainer: {
    marginBottom: 20,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  resultsGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  resultsText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 12,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  internshipCard: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    padding: 24,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  cardHeader: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  companyIconGradient: {
    width: 64,
    height: 64,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  headerInfo: {
    flex: 1,
  },
  internshipTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 6,
  },
  companyName: {
    fontSize: 16,
    color: '#6b7280',
    marginBottom: 10,
    fontWeight: '500',
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  starsContainer: {
    flexDirection: 'row',
    marginRight: 10,
  },
  ratingText: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  description: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
    marginBottom: 20,
  },
  metaInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  metaIconGradient: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  metaText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  progressSection: {
    marginBottom: 20,
  },
  progressTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
    marginBottom: 6,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  moduleCount: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  skillsContainer: {
    marginBottom: 20,
  },
  skillsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#1f2937',
  },
  skillsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillTag: {
    marginRight: 8,
    marginBottom: 8,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  skillTagGradient: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  skillText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#ffffff',
  },
  benefitsContainer: {
    marginBottom: 24,
  },
  benefitsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#1f2937',
  },
  benefitsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 8,
  },
  benefitIconGradient: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 6,
  },
  benefitText: {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: '500',
  },
  cardActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  detailsButton: {
    flex: 0.48,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  detailsButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  detailsButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
    marginLeft: 6,
  },
  applyButton: {
    flex: 0.48,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  applyButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  applyButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#ffffff',
    marginLeft: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyIconGradient: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
  },
});

export default VirtualInternshipScreen;
